import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, BadgeCheck, PackageOpen } from "lucide-react";
import { api, fmtDate } from "@/lib/api";
import { ProductGrid, ProductSkeleton, EmptyState, Countdown } from "@/components/vaulted";
import { mediaSrc } from "@/lib/api";
import { CategoryIcon } from "@/components/CategoryIcon";

export function CategoriesPage() {
  const [cats, setCats] = useState([]);
  useEffect(() => { api.get("/categories").then((r) => setCats(r.data)); }, []);
  return (
    <div className="container-v py-10" data-testid="categories-page">
      <p className="v-eyebrow">Categories</p>
      <h1 className="mt-1.5 text-2xl font-semibold tracking-tight text-white">Browse by category</h1>
      <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cats.map((c, i) => (
          <Link key={c.id} to={`/marketplace?category=${c.slug}`} data-testid={`categories-card-${c.slug}`}
            className="group v-card flex items-start gap-4 p-5 rise hover:border-violet-500/50 transition-[border-color]" style={{ animationDelay: `${i * 40}ms` }}>
            <div className="grid h-11 w-11 shrink-0 place-items-center rounded-lg border border-[#27272A] bg-[#111116]"><CategoryIcon name={c.icon} className="h-5 w-5 text-violet-400" /></div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-white">{c.name}</p>
              <p className="mt-0.5 text-xs text-zinc-500 line-clamp-2">{c.description}</p>
              <p className="mt-2 text-[11px] text-zinc-400">{c.product_count} products</p>
            </div>
            <ArrowRight className="h-4 w-4 text-zinc-600 group-hover:text-violet-400 group-hover:translate-x-0.5 transition-[color,transform]" />
          </Link>
        ))}
      </div>
    </div>
  );
}

export function StorePage() {
  const { slug } = useParams();
  const [s, setS] = useState(null);
  const [items, setItems] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    api.get(`/stores/${slug}`).then((r) => { setS(r.data); return api.get("/products", { params: { seller: r.data.id, limit: 48 } }); })
      .then((r) => setItems(r.data.items)).catch(() => setErr(true));
  }, [slug]);
  if (err) return <div className="container-v py-20"><EmptyState title="Store not found" /></div>;
  return (
    <div data-testid="store-page">
      <div className="border-b border-[#27272A] hero-bg relative"><div className="absolute inset-0 grid-lines" />
        <div className="container-v relative py-10">
          <p className="v-eyebrow">Store</p>
          <h1 data-testid="store-name" className="mt-1.5 flex items-center gap-2 text-2xl font-semibold text-white">{s?.store_name}{s?.is_official && <BadgeCheck className="h-5 w-5 text-violet-400" />}</h1>
          <p className="mt-2 max-w-2xl text-sm text-zinc-400">{s?.description}</p>
          <p className="mt-3 text-xs text-zinc-500">{s?.product_count ?? 0} active listings · Seller since {fmtDate(s?.created_at)}</p>
        </div>
      </div>
      <div className="container-v py-8">
        {items === null ? <ProductSkeleton /> : items.length ? <ProductGrid items={items} /> : <EmptyState icon={PackageOpen} title="No listings yet" />}
      </div>
    </div>
  );
}

export function PromoPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => { api.get(`/promotions/${slug}`).then((r) => setP(r.data)).catch(() => setErr(true)); }, [slug]);
  if (err) return <div className="container-v py-20"><EmptyState title="This promotion has ended" action={<Link to="/marketplace" className="v-btn-primary v-btn-sm">Browse accounts</Link>} /></div>;
  if (!p) return <div className="container-v py-10"><ProductSkeleton /></div>;
  return (
    <div data-testid="promo-landing-page">
      <section className="relative overflow-hidden border-b border-[#27272A] hero-bg">
        {p.image_url && <img src={mediaSrc(p.image_url)} alt="" className="absolute inset-0 h-full w-full object-cover opacity-25" />}
        <div className="container-v relative py-14">
          <p className="v-eyebrow">Promotion</p>
          <h1 className="mt-2 max-w-3xl text-3xl font-semibold tracking-tight text-white sm:text-4xl">{p.title}</h1>
          {p.subtitle && <p className="mt-3 max-w-xl text-zinc-300">{p.subtitle}</p>}
          {p.promo_code && <p className="mt-4 text-sm text-zinc-400">Use code <code className="rounded border border-violet-500/40 bg-violet-500/10 px-2 py-0.5 font-mono text-violet-200">{p.promo_code}</code> at checkout</p>}
          {p.show_countdown && p.ends_at && <Countdown to={p.ends_at} className="mt-5" />}
        </div>
      </section>
      <div className="container-v py-8">{p.products.length ? <ProductGrid items={p.products} testId="promo-products" /> : <EmptyState title="No accounts in this promotion yet" />}</div>
    </div>
  );
}
